import React from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { formatMoney } from "../../i18n/formatNumber";
import { buildStudentSettlementSummary } from "./studentSettlementTotals";

export default function StudentSettlementSummary({ settlement, expandedStudentIds, onToggleStudent, renderStudentDetails, getStudentLinkProps, detailsIdPrefix, onManageFees }) {
  const { t, i18n } = useTranslation("billing");
  const summary = buildStudentSettlementSummary(settlement);
  const money = value => formatMoney(value / 100, i18n.language);
  const unknown = t("studentCosts.unknown", { defaultValue: "Brak danych historycznych" });
  const rows = [...summary.students];
  if (summary.otherCharges.finalAmountCents || summary.otherCharges.unknownHistoryCount) rows.push(summary.otherCharges);
  if (renderStudentDetails) {
    ((settlement.snapshot && settlement.snapshot.skipped) || []).forEach(item => {
      if (!rows.some(row => row.studentId === (item.studentId || null))) {
        rows.push({ studentId: item.studentId || null,
          studentName: item.studentName || item.studentId,
          baseAmountCents: 0,
          discountAmountCents: 0,
          finalAmountCents: 0,
          unknownHistoryCount: 0 });
      }
    });
  }
  const feeAction = (studentId, name) => onManageFees && (
    <button
      type="button"
      className="btn btn-link btn-sm p-0 ml-2 align-baseline"
      title={t('fees.manageFor', { name })}
      aria-label={t('fees.manageFor', { name })}
      onClick={() => onManageFees(studentId)}
    >
      <i className="fas fa-receipt" aria-hidden="true" />
    </button>
  );
  return (
    <div className="table-responsive mb-3 settlement-student-summary">
      <table className="table table-sm">
        <caption className="sr-only">{t("studentCosts.title", { defaultValue: "Koszty uczniów" })}</caption>
        <thead>
          <tr>
            <th scope="col">{t("studentCosts.student", { defaultValue: "Uczeń" })}</th>
            <th scope="col">{t("studentCosts.base", { defaultValue: "Przed zniżką" })}</th>
            <th scope="col">{t("studentCosts.discount", { defaultValue: "Zniżka" })}</th>
            <th scope="col">{t("studentCosts.final", { defaultValue: "Po zniżce" })}</th>
            <th scope="col">{t('fees.title')}</th>
            <th scope="col">{t('studentCosts.total')}</th>
          </tr>
        </thead>
        <tbody>{rows.map(row => {
          const id = row.studentId || 'other';
          const name = row.studentId ? row.studentName : t('fees.clientCharges');
          const expanded = !!expandedStudentIds[id];
          const detailsId = `${detailsIdPrefix}-${id}`;
          const hasLessons = renderStudentDetails && ['charged', 'skipped'].some(kind => ((settlement.snapshot && settlement.snapshot[kind]) || []).some(item => (item.studentId || 'other') === id));
          return (
            <React.Fragment key={id}>
              <tr className="settlement-student-row">
                <th scope="row">
                  {hasLessons ? (
                    <button
                      type="button"
                      className="btn btn-link p-0 settlement-disclosure font-weight-bold"
                      aria-expanded={expanded}
                      aria-controls={detailsId}
                      onClick={() => onToggleStudent(id)}
                    >
                      <span aria-hidden="true" className="mr-2">{expanded ? '▾' : '▸'}</span>{name}
                    </button>
                  ) : name}
                  {row.studentId && getStudentLinkProps && (
                    <Link
                      {...getStudentLinkProps(row.studentId)}
                      className="settlement-person-link ml-2"
                      title={t('openStudent', { name })}
                      aria-label={t('openStudent', { name })}
                    >
                      <i className="fas fa-address-card" aria-hidden="true" />
                    </Link>
                  )}
                </th>
                <td data-label={t('studentCosts.base')}>{row.unknownHistoryCount ? unknown : money(row.baseAmountCents)}</td>
                <td data-label={t('studentCosts.discount')}>{row.unknownHistoryCount ? unknown : money(row.discountAmountCents)}</td>
                <td data-label={t('studentCosts.final')}>{money(row.finalAmountCents - (row.extraFeeTotalCents || 0))}</td>
                <td data-label={t('fees.title')} className="text-nowrap">{money(row.extraFeeTotalCents || 0)}{feeAction(row.studentId, name)}</td>
                <td data-label={t('studentCosts.total')}>{money(row.finalAmountCents)}</td>
              </tr>
              {hasLessons && expanded && (
                <tr id={detailsId} className="settlement-student-detail-row"><td colSpan="6" className="settlement-student-lessons">{renderStudentDetails(id)}</td></tr>
              )}
            </React.Fragment>
          );
        })}
        </tbody>
        <tfoot>
          <tr className="settlement-student-total-row">
            <th scope="row">{t('studentCosts.total')}</th>
            <td data-label={t('studentCosts.base')}>{summary.unknownHistoryCount ? unknown : money(summary.finalAmountCents + summary.knownDiscountCents - summary.extraFeeTotalCents)}</td>
            <td data-label={t('studentCosts.discount')}>{summary.unknownHistoryCount ? unknown : money(summary.knownDiscountCents)}</td>
            <td data-label={t('studentCosts.final')}>{money(summary.finalAmountCents - summary.extraFeeTotalCents)}</td>
            <td data-label={t('fees.title')} className="text-nowrap">{money(summary.extraFeeTotalCents)}{feeAction(null, t('fees.wholeClient'))}</td>
            <td data-label={t('studentCosts.total')}>{money(summary.finalAmountCents)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

StudentSettlementSummary.propTypes = {
  settlement: PropTypes.object.isRequired,
  expandedStudentIds: PropTypes.object,
  onToggleStudent: PropTypes.func,
  renderStudentDetails: PropTypes.func,
  getStudentLinkProps: PropTypes.func,
  detailsIdPrefix: PropTypes.string,
  onManageFees: PropTypes.func,
};
StudentSettlementSummary.defaultProps = {
  onManageFees: null,
  expandedStudentIds: {},
  onToggleStudent: () => {},
  renderStudentDetails: null,
  getStudentLinkProps: null,
  detailsIdPrefix: 'student-costs',
};
